export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';
export type PathParams = Record<string, string | number | undefined>;
export type QueryParams = Record<string, string | number | boolean | undefined>;

interface RequestConfig<T> {
  method: HttpMethod;
  headers?: HeadersInit;
  body?: T;
}

interface ApiError {
  error?: string;
  message?: string;
  errorMessage?: string;
  errorCode?: string;
  statusCode?: number;
  timestamp?: string;
}

function buildUrl(baseUrl: string, pathParams?: PathParams, queryParams?: QueryParams): string {
  let url = baseUrl;

  if (pathParams) {
    url = Object.entries(pathParams).reduce((acc, [key, value]) => acc.replace(`:${key}`, encodeURIComponent(String(value))), url);
  }

  if (queryParams) {
    const searchParams = new URLSearchParams(
      Object.entries(queryParams).reduce(
        (acc, [key, value]) => {
          acc[key] = String(value);
          return acc;
        },
        {} as Record<string, string>,
      ),
    );
    url += `?${searchParams.toString()}`;
  }

  return url;
}

async function handleResponse<TResponse>(response: Response): Promise<TResponse> {
  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');

  if (!response.ok) {
    const errorData = isJson ? await response.json() : await response.text();
    const error = new Error(
      typeof errorData === 'string' ? errorData : (errorData as ApiError).error || (errorData as ApiError).message || response.statusText,
    );
    if (typeof errorData !== 'string') {
      Object.assign(error, errorData);
    }
    throw error;
  }

  if (!isJson) {
    throw new Error(`Expected JSON response but got ${contentType}`);
  }

  return response.json();
}

async function fetchBase<TRequest, TResponse>(
  url: string,
  config: RequestConfig<TRequest>,
  pathParams?: PathParams,
  queryParams?: QueryParams,
): Promise<TResponse> {
  try {
    const finalUrl = buildUrl(url, pathParams, queryParams);
    let headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...config.headers,
    };
    let body: string | FormData | undefined;

    if (config.body instanceof FormData) {
      headers = {};
      body = config.body;
    } else if (config.body) {
      body = JSON.stringify(config.body);
    }

    const response = await fetch(finalUrl, {
      method: config.method,
      headers,
      body,
    });

    return handleResponse<TResponse>(response);
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Network request failed');
  }
}

export async function fetchGet<TResponse>(url: string, pathParams?: PathParams, queryParams?: QueryParams): Promise<TResponse> {
  return fetchBase<never, TResponse>(url, { method: 'GET' }, pathParams, queryParams);
}

export async function fetchPost<TRequest, TResponse>(url: string, request?: TRequest, pathParams?: PathParams): Promise<TResponse> {
  return fetchBase<TRequest, TResponse>(url, { method: 'POST', body: request }, pathParams);
}

export async function fetchPut<TRequest, TResponse>(url: string, request?: TRequest, pathParams?: PathParams): Promise<TResponse> {
  return fetchBase<TRequest, TResponse>(url, { method: 'PUT', body: request }, pathParams);
}

export async function fetchDelete<TRequest, TResponse>(url: string, request?: TRequest, pathParams?: PathParams): Promise<TResponse> {
  return fetchBase<TRequest, TResponse>(url, { method: 'DELETE', body: request }, pathParams);
}
